import { MAX_UPLOAD_BYTES, MAX_VOICE_MS, type AttachmentDto } from '@dijitalapp/shared';
import { useEffect, useRef, useState } from 'react';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';

const url = (a: AttachmentDto, thumb = false) => `/api/v1/attachments/${a.id}${thumb ? '/thumb' : ''}`;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Renders an image, voice note or file chip inside a message bubble. */
export function AttachmentView({ attachment: a }: { attachment: AttachmentDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (a.kind === 'image') {
    // Reserve the box from known dimensions so the list does not jump while loading.
    const ratio = a.width && a.height ? `${a.width} / ${a.height}` : undefined;
    return (
      <a className="attachment-image" href={url(a)} target="_blank" rel="noopener noreferrer">
        <img src={url(a, a.hasThumbnail)} alt={a.name} loading="lazy" style={ratio ? { aspectRatio: ratio } : undefined} />
      </a>
    );
  }
  if (a.kind === 'video') {
    // preload=metadata shows the first frame; the server answers Range requests for seeking.
    return <video className="attachment-video" controls playsInline preload="metadata" src={url(a)} aria-label={a.name} />;
  }
  if (a.kind === 'voice') {
    return (
      <span className="attachment-voice">
        <audio controls preload="metadata" src={url(a)} aria-label={t('chat.voiceMessage')} />
        {a.durationMs !== null && <span className="hint">{formatDuration(a.durationMs)}</span>}
      </span>
    );
  }
  return (
    <a className="attachment-file" href={url(a)} download={a.name}>
      <Icon name="file" />
      <span className="truncate">{a.name}</span>
      <span className="hint">{formatSize(a.size)}</span>
      <span className="visually-hidden">{t('chat.download')}</span>
    </a>
  );
}

export const ACCEPTED_FILES =
  'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,application/pdf,.docx,.xlsx,.pptx,.odt,.ods,.odp,.txt,.csv';

export function tooLarge(file: File): boolean {
  return file.size > MAX_UPLOAD_BYTES;
}

function pickMimeType(): string | undefined {
  const options = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return options.find((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m));
}

/**
 * Voice note recorder. Starts recording on mount; "stop" hands the clip over, "cancel" discards it.
 * The microphone is released in every path, including unmount.
 */
export function VoiceRecorder({
  onDone,
  onCancel,
  onError,
}: {
  onDone: (clip: Blob, durationMs: number, extension: string) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const keep = useRef(false);
  const startedAt = useRef(0);
  const callbacks = useRef({ onDone, onCancel, onError });
  useEffect(() => {
    callbacks.current = { onDone, onCancel, onError };
  });

  useEffect(() => {
    let cancelled = false;
    const chunks: Blob[] = [];
    const release = () => stream.current?.getTracks().forEach((track) => track.stop());
    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((track) => track.stop());
          return;
        }
        stream.current = s;
        const mimeType = pickMimeType();
        const r = new MediaRecorder(s, mimeType ? { mimeType } : undefined);
        recorder.current = r;
        r.ondataavailable = (e) => {
          if (e.data.size) chunks.push(e.data);
        };
        r.onstop = () => {
          release();
          if (!keep.current) return;
          const type = r.mimeType || 'audio/webm';
          const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
          callbacks.current.onDone(new Blob(chunks, { type }), Date.now() - startedAt.current, ext);
        };
        startedAt.current = Date.now();
        r.start(1000);
      })
      .catch(() => callbacks.current.onError(t('chat.micDenied')));
    const timer = setInterval(() => {
      if (!startedAt.current) return;
      const ms = Date.now() - startedAt.current;
      setElapsed(ms);
      if (ms >= MAX_VOICE_MS) {
        keep.current = true;
        recorder.current?.stop();
      }
    }, 250);
    return () => {
      cancelled = true;
      clearInterval(timer);
      if (recorder.current?.state === 'recording') recorder.current.stop();
      release();
    };
  }, [t]);

  const finish = (send: boolean) => {
    keep.current = send;
    if (recorder.current?.state === 'recording') recorder.current.stop();
    if (!send) callbacks.current.onCancel();
  };

  return (
    <div className="voice-recorder" role="status">
      <span className="recording-dot" aria-hidden />
      <span>{t('chat.recording', { seconds: Math.floor(elapsed / 1000) })}</span>
      <span className="spacer" />
      <button className="btn" type="button" onClick={() => finish(false)}>
        {t('chat.cancelRecording')}
      </button>
      <button className="btn btn-primary" type="button" onClick={() => finish(true)}>
        <Icon name="stop" /> {t('chat.stopRecording')}
      </button>
    </div>
  );
}
